import "./bootstrap";
import "../css/app.css";
import "./echo";

import { createRoot } from "react-dom/client";
import { createInertiaApp } from "@inertiajs/react";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";
import { useEffect, ReactNode } from "react";
import { createContext, useState, useContext } from "react";
import { router } from "@inertiajs/core";
import { Toaster } from "@/shadcn/ui/toaster";
import { toast } from "@/shadcn/ui/use-toast";
import { Toaster as SonnerToaster } from "sonner";

// Import i18next and react-i18next
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// Import your translation files
import enTranslation from "./lib/locales/en/translation.json";
import ptTranslation from "./lib/locales/pt/translation.json";

// Import ChartJS components
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

// Initialize i18next
i18n.use(initReactI18next).init({
    resources: {
        en: {
            translation: enTranslation,
        },
        pt: {
            translation: ptTranslation,
        },
    },
    lng: "pt", // default language
    fallbackLng: "pt",
    interpolation: {
        escapeValue: false, // React already escapes by default
    },
});

ChartJS.register(ArcElement, Tooltip, Legend);

const appName = "CryptoTax";

interface ThemeContextType {
    isDark: boolean;
    toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextType>({
    isDark: true,
    toggleTheme: () => {},
});

export function useTheme(): ThemeContextType {
    return useContext(ThemeContext);
}

function initializeTheme() {
    // Always force dark mode, ignore any saved preferences
    document.documentElement.classList.add("dark");
    localStorage.removeItem("theme");
    return true;
}

initializeTheme();

export const BroadcastContext = createContext<{
    walletUpdating: boolean;
    setWalletUpdating: React.Dispatch<React.SetStateAction<boolean>>;
}>({
    walletUpdating: false,
    setWalletUpdating: () => {},
});

interface AppWrapperProps {
    children: ReactNode;
    user?: {
        id: number;
        name: string;
        email: string;
    };
    initialWalletUpdating: boolean;
}

function AppWrapper({
    children,
    user,
    initialWalletUpdating,
}: AppWrapperProps) {
    const [walletUpdating, setWalletUpdating] = useState(initialWalletUpdating);
    const [isDark, setIsDark] = useState(() => {
        const savedTheme = localStorage.getItem("theme");
        return savedTheme !== "light"; // Default to dark unless explicitly set to light
    });

    const toggleTheme = () => {
        const newIsDark = !isDark;
        setIsDark(newIsDark);
        if (newIsDark) {
            document.documentElement.classList.add("dark");
            localStorage.removeItem("theme"); // Remove item to use default dark
        } else {
            document.documentElement.classList.remove("dark");
            localStorage.setItem("theme", "light"); // Only persist light mode preference
        }
    };

    useEffect(() => {
        if (!user?.id) return;

        const channel = window.Echo.private(`user.${user.id}`);

        channel.listen(".sync.finished", (event: { status: string }) => {
            if (event.status === "success" || event.status === "error") {
                setWalletUpdating(false);
                router.reload();
            }
        });

        channel.listen(".csv.processing.complete", (event: { success: boolean; exchange_name: string; error_message?: string }) => {
            if (event.success) {
                toast({
                    title: "CSV processado com sucesso",
                    description: `CSV de ${event.exchange_name} foi processado com sucesso`,
                    duration: 5000,
                });
            } else {
                toast({
                    title: "Erro ao processar CSV",
                    description: event.error_message || "Ocorreu um erro ao processar o CSV",
                    variant: "destructive",
                    duration: 5000,
                });
            }
            router.reload();
        });

        return () => {
            channel.stopListening(".sync.finished");
            channel.stopListening(".csv.processing.complete");
        };
    }, [user?.id]);

    return (
        <ThemeContext.Provider value={{ isDark, toggleTheme }}>
            <BroadcastContext.Provider
                value={{ walletUpdating, setWalletUpdating }}
            >
                {children}
                <Toaster />
                <SonnerToaster />
            </BroadcastContext.Provider>
        </ThemeContext.Provider>
    );
}

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) =>
        resolvePageComponent(
            `./Pages/${name}.tsx`,
            import.meta.glob("./Pages/**/*.tsx"),
        ),
    setup({ el, App, props }) {
        const root = createRoot(el);

        interface InertiaPageProps {
            auth?: {
                user?: {
                    id: number;
                    name: string;
                    email: string;
                };
            };
            walletUpdating?: boolean;
        }

        const pageProps = props.initialPage.props as InertiaPageProps;

        root.render(
            <AppWrapper
                user={pageProps.auth?.user}
                initialWalletUpdating={pageProps.walletUpdating || false}
            >
                <App {...props} />
            </AppWrapper>,
        );
    },
    progress: {
        color: "#4B5563",
        includeCSS: true,
        showSpinner: false,
    },
});
